import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ToggleButton } from "primereact/togglebutton";
import { Checkbox } from "primereact/checkbox";
import { Image } from "primereact/image";
import { Button } from "primereact/button";
import { findNonSerializableValue } from "@reduxjs/toolkit";
import { FaGears, FaRightFromBracket, FaRightToBracket } from "react-icons/fa6";
import CheckMenu from "./CheckMenu";
import { useContext, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { format } from "date-fns";
import UiContext from "../../../../store/ui-context";
import useAPIRequest from "../../../../custom_hooks/simple/useAPIRequest";
import SavedBadge from "../common/SavedBadge";
import { getSavedStatus } from "../../utils/editorHelpers";
import ChecklistProblemDialog from "../checklist/ChecklistProblemDialog";
import ChecklistRowDetail from "../checklist/ChecklistRowDetail";
import useIsMobile from "../../../../hooks/useIsMobile";
import {
  findMissingNewProblems,
  findProblem,
  isCopiedFromBerangkat,
  isIgnoredProblem,
  isMissingRequiredGambar,
  isRequiredGambar,
  isNewProblem,
  isSameGambar,
} from "../../utils/checklistProblem";

const CheckList = (props) => {
  const {
    // params,
    labelGrup,
    pengecekanBerangkatData,
    setPengecekanBerangkatData,
    pengecekanKembaliData,
    setPengecekanKembaliData,
    pengecekanBerangkatDetilData,
    setPengecekanBerangkatDetilData,
    pengecekanKembaliDetilData,
    setPengecekanKembaliDetilData,
    title,
    showBerangkat,
    isBerangkat,
    disabled,
    onClickSaveBerangkat,
    onClickSaveKembali,
    onClickCameraBerangkat,
    onClickCameraKembali,
    onClickDeskripsi,
    onClickFotoRow,
    loading,
    modeAmbilGambar,
    setModeAmbilGambar,
    onClickReload,
    listProblemData,
    onProblemResolved,
    folderName,
    saveDisabled,
  } = props;
  const uiContext = useContext(UiContext);
  const { requestPost } = useAPIRequest();
  const overlayRefs = useRef({});
  const op = useRef(null);
  const [rowData, setRowData] = useState(null);
  // the note + gambar dialog: { side: "berangkat" | "kembali", detil, mode: "check" | "new-problem", problem }
  const [dialogTarget, setDialogTarget] = useState(null);
  // set when a save was blocked: the new problems that still miss note / gambar get a red hint
  const [highlightMissing, setHighlightMissing] = useState(false);
  // the open problems of the rows checked through the dialog, resolved when the card is saved:
  // { "<side>:<detil id>": problem }
  const [pendingResolve, setPendingResolve] = useState({});
  // rows whose detail was opened / closed by hand: { keterangan: true | false }
  const [expandedRows, setExpandedRows] = useState({});
  const isMobile = useIsMobile();

  // other methods

  // merges `patch` into the detil `id` of one side
  const updateDetil = (side, id, patch) => {
    const list =
      side === "berangkat"
        ? pengecekanBerangkatDetilData
        : pengecekanKembaliDetilData;
    const setList =
      side === "berangkat"
        ? setPengecekanBerangkatDetilData
        : setPengecekanKembaliDetilData;
    // _dirty: changed on this page, not saved yet (cleared when the saved row comes back from the server)
    setList(
      list.map((el) => (el.id === id ? { ...el, ...patch, _dirty: true } : el))
    );

    // unchecked again: its problem is not resolved after all
    if (patch.cek === 0) {
      setPendingResolve((prev) => {
        const { [`${side}:${id}`]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  // a box was (un)checked. Checking a row that has a problem first asks note + gambar in the dialog.
  const handleToggleCek = (side, detil, checked) => {
    if (!detil) return;
    const problem = findProblem(listProblemData, detil);

    // kembali: the same row was already checked on berangkat, so it is not asked again. On berangkat a row with a
    // problem could only be checked through this dialog; older data (checked before the dialog existed) has no
    // note / gambar, so those are not required here.
    const berangkatDetil =
      side === "kembali" && pengecekanBerangkatDetilData
        ? pengecekanBerangkatDetilData.find(
            (el) =>
              el.keterangan === detil.keterangan &&
              el.label_grup === detil.label_grup
          )
        : null;
    const alreadyAnswered = !!berangkatDetil && berangkatDetil.cek === 1;

    if (checked && problem && !alreadyAnswered) {
      // the note copied from berangkat is not a kembali note: the dialog starts empty
      const isCopiedNote =
        side === "kembali" &&
        isCopiedFromBerangkat({
          detil,
          berangkatDetils: pengecekanBerangkatDetilData,
        });
      setDialogTarget({
        side,
        detil: isCopiedNote ? { ...detil, note: "" } : detil,
        mode: "check",
        problem,
      });
    } else {
      updateDetil(side, detil.id, { cek: checked ? 1 : 0 });
    }
  };

  const handleOnConfirmDialog = ({ note, gambar }) => {
    const { side, detil, mode, problem } = dialogTarget;
    updateDetil(
      side,
      detil.id,
      mode === "check" ? { cek: 1, note, gambar } : { note, gambar }
    );
    if (mode === "check" && problem) {
      setPendingResolve((prev) => ({ ...prev, [`${side}:${detil.id}`]: problem }));
    }
    setDialogTarget(null);
  };

  // resolves the problem of a row that was checked through the dialog: status 3, tanggal_selesai = now, and the
  // note + gambar of the row
  const resolveProblem = (problem, detil) => {
    requestPost({
      // /update merges onto the existing record, /save would overwrite the whole row
      fullUrl: "/api/problem/problemkendaraan/update",
      body: {
        id: problem.id,
        status: 3,
        tanggal_selesai: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        note: detil.note,
        gambar: detil.gambar ? detil.gambar.split("?serial=")[0] : detil.gambar,
      },
      isForm: false,
      onError: ({ message }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: () => {
        if (onProblemResolved) onProblemResolved(problem.id);
      },
    });
  };

  // Save: an unchecked row without an open problem is a new problem, it needs a note and a gambar first
  const handleOnClickSave = () => {
    const detils = showBerangkat
      ? getKembaliDetilData(labelGrup)
      : getBerangkatDetilData(labelGrup);

    // showBerangkat: the kembali rows are saved, they are compared with the berangkat rows
    const missing = findMissingNewProblems({
      detils,
      listProblemData,
      berangkatDetils: showBerangkat ? pengecekanBerangkatDetilData : undefined,
    });
    if (missing.length > 0) {
      setHighlightMissing(true);
      uiContext.showErrorMsg(
        "Error",
        `Problem baru wajib diisi note dan gambar: ${missing
          .map((el) => el.keterangan)
          .join(", ")}`
      );
      return;
    }

    // a row whose option needs a photo must have one (its "Foto wajib diisi" hint is shown on the row)
    const missingGambar = detils.filter(isMissingRequiredGambar);
    if (missingGambar.length > 0) {
      setHighlightMissing(true);
      uiContext.showErrorMsg(
        "Error",
        `Foto wajib diisi: ${missingGambar
          .map((el) => el.keterangan)
          .join(", ")}`
      );
      return;
    }
    setHighlightMissing(false);

    // the rows checked through the dialog resolve their problem together with the save
    const side = showBerangkat ? "kembali" : "berangkat";
    detils.forEach((detil) => {
      const problem = pendingResolve[`${side}:${detil.id}`];
      if (problem && detil.cek === 1) resolveProblem(problem, detil);
    });
    setPendingResolve((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !key.startsWith(`${side}:`))
      )
    );

    if (showBerangkat) {
      onClickSaveKembali(detils);
    } else {
      onClickSaveBerangkat(detils);
    }
  };

  const getBerangkatDetilData = (label_grup) => {
    const _result = pengecekanBerangkatDetilData
      ? pengecekanBerangkatDetilData
        .filter((el) => el.label_grup === label_grup)
        .sort((a, b) => a.urutan - b.urutan)
      : [];

    return _result;
  };

  const getKembaliDetilData = (label_grup) => {
    return pengecekanKembaliDetilData
      ? pengecekanKembaliDetilData
        .filter((el) => el.label_grup === label_grup)
        .sort((a, b) => a.urutan - b.urutan)
      : [];
  };

  /**
   * Generate Table Body Template
   **/

  const cekBerangkatBodyTemplate = (row) => {
    const { keterangan } = row;

    const dataBerangkat = pengecekanBerangkatDetilData.find(
      (el) => el.keterangan === keterangan
    );

    return (
      <div className="row-center-center">
        <Checkbox
          disabled={disabled || showBerangkat}
          className="!p-0"
          checked={!dataBerangkat ? true : dataBerangkat.cek === 1}
          onChange={(e) =>
            handleToggleCek("berangkat", dataBerangkat, e.target.checked)
          }
        />
      </div>
    );
  };

  const cekKembaliBodyTemplate = (row) => {
    const { keterangan } = row;

    const dataKembali = pengecekanKembaliDetilData.find(
      (el) => el.keterangan === keterangan
    );

    return (
      <div className="row-center-center">
        <Checkbox
          disabled={disabled}
          className="!p-0"
          checked={!dataKembali ? true : dataKembali.cek === 1}
          onChange={(e) =>
            handleToggleCek("kembali", dataKembali, e.target.checked)
          }
        />
      </div>
    );
  };

  // const imageBerangkatBodyTemplate = (row) => {
  //   const { description, field } = row;
  //   let imageBerangkat = null;
  //   if (pengecekanBerangkatDetilData && pengecekanBerangkatDetilData[field]) {
  //     imageBerangkat = pengecekanBerangkatDetilData[field];
  //   }
  //   return (
  //     <div className="row-center-center ">
  //       {imageBerangkat && (
  //         <Image
  //           className="bg-white shadow-md"
  //           src={`${imageBerangkat}?serial=${Math.random()}`}
  //           alt={`berangkat_${description}`}
  //           width="100"
  //           preview
  //         />
  //       )}
  //       {!imageBerangkat && (
  //         <Button
  //           disabled={disabled}
  //           icon="pi pi-camera"
  //           rounded
  //           className="bg-slate-300 border-slate-300 max-h-[2.2rem] max-w-[2.2rem]"
  //           pt={{ icon: { className: "!text-lg" } }}
  //           onClick={() => onClickCameraBerangkat(row)}
  //         />
  //       )}
  //     </div>
  //   );
  // };

  // const imageKembaliBodyTemplate = (row) => {
  //   const { description, field } = row;
  //   let imageKembali = null;
  //   if (pengecekanKembaliDetilData && pengecekanKembaliDetilData[field]) {
  //     imageKembali = pengecekanKembaliDetilData[field];

  //   }
  //   return (
  //     <div className="row-center-center ">
  //       {imageKembali && (
  //         <Image
  //           className="bg-white shadow-md"
  //           src={`${imageKembali}?serial=${Math.random()}`}
  //           alt={`kembali_${description}`}
  //           width="100"
  //           preview
  //         />
  //       )}
  //       {!imageKembali && (
  //         <Button
  //           disabled={disabled}
  //           icon="pi pi-camera"
  //           rounded
  //           className="bg-slate-300 border-slate-300 max-h-[2.2rem] max-w-[2.2rem]"
  //           pt={{ icon: { className: "!text-lg" } }}
  //           onClick={() => onClickCameraKembali(row)}
  //         />
  //       )}
  //     </div>
  //   );
  // };

  const descriptionBodyTemplate = (row) => {
    const { keterangan } = row;

    // "Pergi" (below) always wants the genuine berangkat row for this keterangan, regardless of which side is
    // actually being shown (see the "Pergi" toggle: it can swap `value` to the other side's rows)
    const dataBerangkat = pengecekanBerangkatDetilData.find(
      (el) => el.keterangan === keterangan
    );

    // `row` is already the exact detil being rendered (whichever side that is), so its own fields are read
    // directly from it - looking them up again by keterangan risked missing on the side not currently shown
    const currentData = row;
    const note = currentData.note ?? "";
    const currentImage = currentData.gambar;
    const rowMissingRequiredGambar = isMissingRequiredGambar(currentData);

    // open problem of this row (shown with a warning icon), and whether it is a new problem that misses note / gambar
    const problem = findProblem(listProblemData, currentData);
    const isMissingNewProblem =
      highlightMissing &&
      findMissingNewProblems({
        detils: [currentData],
        listProblemData,
        berangkatDetils: isBerangkat ? undefined : pengecekanBerangkatDetilData,
      }).length > 0;

    // On kembali the row has three separate parts, told apart with a label:
    //   the problem (its own note / photo), "Pergi" (the berangkat note / photo, follows the "Pergi" toggle) and
    //   "Pulang" (what is filled in now). Berangkat page: only the problem and the row itself.
    // The problem holds the same note / photo as the berangkat row it was created from, and the kembali row copies the
    // berangkat note, so a note / photo is only shown in the first part it belongs to.
    const onKembali = !isBerangkat;
    const isSameNote = (a, b) => !!a && !!b && a.trim() === b.trim();

    let pergiNote =
      onKembali && showBerangkat && dataBerangkat ? dataBerangkat.note : null;
    let pergiImage =
      onKembali && showBerangkat && dataBerangkat ? dataBerangkat.gambar : null;
    let pulangNote = note;
    let pulangImage = currentImage;

    // the kembali note is copied from berangkat when the page loads: that copy is the berangkat note (shown under
    // "Pergi"), not something written on kembali
    if (
      onKembali &&
      isCopiedFromBerangkat({
        detil: currentData,
        berangkatDetils: pengecekanBerangkatDetilData,
      })
    ) {
      pulangNote = null;
    }

    if (problem) {
      if (isSameNote(pergiNote, problem.note)) pergiNote = null;
      if (isSameNote(pulangNote, problem.note)) pulangNote = null;
      if (isSameGambar(pergiImage, problem.gambar)) pergiImage = null;
      if (isSameGambar(pulangImage, problem.gambar)) pulangImage = null;
    }
    if (isSameNote(pergiNote, pulangNote)) pergiNote = null;
    if (isSameGambar(pergiImage, pulangImage)) pergiImage = null;

    let problemDayCount = 0;
    if (problem) {
      problemDayCount = Math.floor(
        (new Date() - new Date(problem.tanggal)) / (1000 * 60 * 60 * 24)
      );
    }
    const problemSince = problem
      ? `Problem sejak ${format(new Date(problem.tanggal), "dd/MM/yyyy")}${
          problemDayCount > 0 ? ` (${problemDayCount} hari)` : ""
        }`
      : "";

    // an unchecked row that is a new problem (see isNewProblem): its note / photo are the new problem's
    const rowIsNewProblem =
      !!currentData &&
      currentData.cek !== 1 &&
      isNewProblem({
        detil: currentData,
        listProblemData,
        berangkatDetils: onKembali ? pengecekanBerangkatDetilData : undefined,
      });
    const pulangLabel = rowIsNewProblem
      ? "Problem baru"
      : onKembali
      ? "Pulang"
      : null;

    const altName = keterangan.replace(" ", "_");

    // the detail (problem lama / pergi / pulang) is closed on a phone: with many rows the photos make the list too long
    // to walk along the vehicle. Desktop starts open. Tap "Detail" to change it per row.
    const photoCount = [problem && problem.gambar, pergiImage, pulangImage].filter(
      Boolean
    ).length;
    const noteCount = [problem && problem.note, pergiNote, pulangNote].filter(
      Boolean
    ).length;
    // a problem with neither note nor gambar still needs to be reachable (at least the "sejak ... hari" line),
    // so its mere existence counts toward detailCount too - this doesn't change the photo/note badge counts shown
    const detailCount = photoCount + noteCount + (problem ? 1 : 0);
    const isRowExpanded = expandedRows[keterangan] ?? !isMobile;

    return (
      // select-none: tapping the row / the dots twice would otherwise select the whole row (blue highlight)
      <div className="col-start-start gap-2 cursor-pointer w-full select-none">
        <div className="row-between-start w-full gap-2">
          <span
            onClick={(e) => {
              // op.current.toggle(e);
              setRowData(row);
              if (onClickDeskripsi) onClickDeskripsi(e, row);
            }}
          >
            {keterangan}
            {isRequiredGambar(currentData) && (
              <span className="text-red-600 font-bold ml-1" title="Foto wajib diisi">
                *
              </span>
            )}
          </span>
          <div className="row-start-center gap-2 shrink-0">
            {problem && (
              <span
                className="row-start-center gap-1"
                title={`${problemSince}${
                  isIgnoredProblem(problem) ? " - diabaikan" : ""
                }`}
              >
                {isIgnoredProblem(problem) && (
                  <span className="text-xs font-semibold text-slate-400">
                    Ignore
                  </span>
                )}
                <i
                  className={`pi pi-exclamation-triangle ${
                    isIgnoredProblem(problem)
                      ? "text-slate-400"
                      : "text-red-500"
                  }`}
                ></i>
              </span>
            )}
            {/* the three dots tell that the row has actions (note / foto berangkat / foto pulang), same as
            tapping the description.
            w-10: the menu's arrow tip is drawn 1.25rem from the left edge of the clicked element (OverlayPanel),
            so a 2.5rem wide button has the dots right under the tip. -mr-2 keeps the dots near the row's edge */}
            {onClickDeskripsi && !disabled && (
              <button
                type="button"
                aria-label={`Aksi ${keterangan}`}
                className="row-center-center bg-transparent border-none p-0 w-10 h-8 -mr-2 text-slate-500 cursor-pointer"
                onClick={(e) => {
                  setRowData(row);
                  onClickDeskripsi(e, row);
                }}
              >
                <i className="pi pi-ellipsis-v"></i>
              </button>
            )}
          </div>
        </div>

        {/* what the row has: tap to open / close, the counts tell what is inside without opening it */}
        {detailCount > 0 && (
          <button
            type="button"
            className="row-start-center gap-2 text-xs text-slate-500 bg-transparent border-none p-0 cursor-pointer"
            onClick={() =>
              setExpandedRows((prev) => ({
                ...prev,
                [keterangan]: !isRowExpanded,
              }))
            }
          >
            <i
              className={`pi ${
                isRowExpanded ? "pi-chevron-up" : "pi-chevron-down"
              } text-[10px]`}
            ></i>
            <span className="font-semibold">Detail</span>
            {photoCount > 0 && (
              <span className="row-start-center gap-1">
                <i className="pi pi-image text-[11px]"></i>
                {photoCount}
              </span>
            )}
            {noteCount > 0 && (
              <span className="row-start-center gap-1">
                <i className="pi pi-comment text-[11px]"></i>
                {noteCount}
              </span>
            )}
          </button>
        )}

        {detailCount > 0 && isRowExpanded && (
          <div className="col-start-start gap-2 w-full">
            {problem && (
              <ChecklistRowDetail
                className="rounded bg-red-100 px-2 py-1.5"
                label={`Problem lama - ${problemSince.replace("Problem ", "")}`}
                labelClassName="text-red-600"
                noteClassName="text-red-500"
                note={problem.note}
                gambar={problem.gambar}
                alt={`problem_${altName}`}
              />
            )}
            {(pergiNote || pergiImage) && (
              <ChecklistRowDetail
                label="Pergi"
                note={pergiNote}
                gambar={pergiImage}
                alt={`berangkat_${altName}`}
              />
            )}
            {(pulangNote || pulangImage) && (
              <ChecklistRowDetail
                label={pulangLabel}
                labelClassName={
                  rowIsNewProblem ? "text-red-600" : "text-slate-400"
                }
                note={pulangNote}
                gambar={pulangImage}
                alt={`${onKembali ? "kembali" : "berangkat"}_${altName}`}
              />
            )}
          </div>
        )}

        {/* what still has to be filled in stays visible, also when the detail is closed */}
        {isMissingNewProblem && (
          <span
            className="text-xs text-red-600 font-semibold row-start-center gap-1 hover:underline"
            onClick={() =>
              setDialogTarget({
                side: isBerangkat ? "berangkat" : "kembali",
                // the note copied from berangkat is not a note of this problem: start empty
                detil: {
                  ...currentData,
                  note: pulangNote ?? "",
                },
                mode: "new-problem",
                problem: undefined,
              })
            }
          >
            <i className="pi pi-pencil text-xs"></i> Note & foto wajib diisi
          </span>
        )}
        {/* like "Note & foto wajib diisi": only shown after a save was blocked, the * next to the description is
        the permanent marker of a row that needs a photo */}
        {highlightMissing && rowMissingRequiredGambar && (
          <span
            className="text-xs text-red-600 font-semibold row-start-center gap-1 underline"
            // takes the photo of this page's side right away (Berangkat on the berangkat page, Pulang on kembali)
            onClick={(e) => {
              setRowData(row);
              if (onClickFotoRow) onClickFotoRow(row);
              else if (onClickDeskripsi) onClickDeskripsi(e, row);
            }}
          >
            <i className="pi pi-camera text-xs"></i> Foto wajib diisi
          </span>
        )}

        {/* {(!imageBerangkat || !imageKembali) && modeAmbilGambar && (
          <div className="row-start-start gap-2">
            {!imageBerangkat && (
              <Button
                disabled={disabled}
                icon="pi pi-camera"
                text
                label="Berangkat"
                className="max-h-[1.5rem] !mx-0 !pl-0"
                onClick={() => onClickCameraBerangkat(row)}
              />
            )}
            {showBerangkat && (
              <Button
                disabled={disabled}
                icon="pi pi-camera"
                text
                label="Pulang"
                className="max-h-[1.5rem] !mx-0 !pl-0"
                onClick={() => onClickCameraKembali(row)}
              />
            )}
          </div>
        )} */}
      </div>
    );
  };

  // the rows of the page's own side (berangkat page: berangkat, kembali page: kembali): saved when none of them
  // changed since the last save / load and something was checked, unsaved as soon as one of them changed
  const ownRows = isBerangkat
    ? getBerangkatDetilData(labelGrup)
    : getKembaliDetilData(labelGrup);
  const savedStatus = getSavedStatus(
    ownRows,
    ownRows.some((detil) => detil.cek === 1)
  );

  return (
    <div className="col-start-start gap-2 w-full">
      <ChecklistProblemDialog
        visible={!!dialogTarget}
        mode={dialogTarget?.mode}
        detil={dialogTarget?.detil}
        problem={dialogTarget?.problem}
        folderName={folderName}
        onConfirm={handleOnConfirmDialog}
        onHide={() => setDialogTarget(null)}
      />
      {/* <OverlayPanel
        className=""
        ref={op}
        pt={{ content: { className: "col-start-center py-1 px-0" } }}
      > */}
      {/* <CheckMenu
        row={rowData}
        onClickCameraBerangkat={onClickCameraBerangkat}
        onClickCameraKembali={onClickCameraKembali}
        pengecekanBerangkatDetilData={pengecekanBerangkatDetilData}
        pengecekanKembaliDetilData={pengecekanKembaliDetilData}
        isBerangkat={isBerangkat}
        handleOnClickReload={handleOnClickReload}
        onClickDeskripsi={onClickDeskripsi}
        keterangan =
      /> */}
      {/* </OverlayPanel> */}
      <DataTable
        loading={loading}
        className="w-full"
        //   showHeaders={false}
        // `data` here is the row itself (from `value` below), whichever side that actually is (see the
        // "Pergi" toggle) - reading its own `cek` directly avoids looking it up on the wrong side's list,
        // which crashed when that side had no row for this keterangan yet
        rowClassName={(data) => (data.cek ? "bg-green-50" : "bg-red-50")}
        header={
          <div className="row-between-center">
            <div className="row-start-center gap-3">
              <span className="text-sm"> {title.toUpperCase()} </span>
              <SavedBadge status={savedStatus} />
            </div>
            <Button
              loading={loading}
              className="!p-0 max-h-[1.3rem] max-w-[1.3rem]"
              icon="pi pi-refresh"
              text
              rounded
              onClick={onClickReload}
            />
            {/* <ToggleButton
              onLabel="Foto"
              offLabel="Foto"
              checked={modeAmbilGambar}
              onChange={(e) => setModeAmbilGambar((prev) => !prev)}
              onIcon="pi pi-eye"
              offIcon="pi pi-eye-slash"
            /> */}
          </div>
        }
        footer={
          <div className="row-end-center">
            <Button
              loading={loading}
              disabled={disabled || saveDisabled}
              icon="pi pi-save"
              label={`Save ${title}`}
              severity="success"
              onClick={handleOnClickSave}
            />
          </div>
        }
        value={
          showBerangkat
            ? getKembaliDetilData(labelGrup)
            : getBerangkatDetilData(labelGrup)
        }
      >
        {(isBerangkat || showBerangkat) && (
          <Column
            header={FaRightFromBracket}
            headerClassName="text-xs"
            className="w-[2rem] !p-0"
            alignHeader={"center"}
            body={cekBerangkatBodyTemplate}
          />
        )}

        {!isBerangkat && (
          <Column
            header={FaRightToBracket}
            headerClassName="text-xs"
            className="w-[3rem] !p-0  border-l-2"
            alignHeader={"center"}
            body={cekKembaliBodyTemplate}
          />
        )}

        <Column
          header="DESKRIPSI"
          headerClassName="text-xs"
          className="border-l-2"
          body={descriptionBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default CheckList;
